import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { SocialLinks } from "@/components/store/SocialLinks";

export const Footer = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({});

  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { name, email });
      toast.success("You're on the list!", { description: "We'll email you when the doors open." });
      setName(""); setEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-brand-navy text-white mt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <div className="inline-block bg-white rounded-2xl p-3">
            <img src={mediaUrl(settings.logo_url || "/images/logo.png")} alt={settings.business_name || "Barrhaven Dealswagon Outlet"} className="h-16 w-auto" />
          </div>
          <p className="mt-4 max-w-md text-slate-300">
            Barrhaven's drive-through discount outlet — great deals on brand-name products across every category. Shop online, drive through, save big.
          </p>
          <form onSubmit={submit} className="mt-6 space-y-2 max-w-md">
            <div className="font-heading text-lg text-brand-yellow">GET DEAL ALERTS</div>
            <p className="text-sm text-slate-300">Be the first to hear about new arrivals, special deals and opening updates.</p>
            <div className="flex flex-col sm:flex-row gap-2">
              <input data-testid="footer-newsletter-name" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Name" className="flex-1 px-4 h-11 rounded-full text-slate-900 text-sm" />
              <input data-testid="footer-newsletter-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address" className="flex-1 px-4 h-11 rounded-full text-slate-900 text-sm" />
              <button data-testid="footer-newsletter-submit" disabled={loading}
                className="bg-brand-red hover:bg-brand-redHover disabled:opacity-60 font-bold px-5 h-11 rounded-full active:scale-95 transition">
                {loading ? "..." : "Notify Me"}
              </button>
            </div>
          </form>
        </div>

        <div>
          <div className="font-heading text-brand-yellow mb-3">Shop</div>
          <ul className="space-y-2 text-sm text-slate-300">
            <li><Link to="/shop" className="hover:text-white">All Products</Link></li>
            <li><Link to="/deals" className="hover:text-white">Today's Deals</Link></li>
            <li><Link to="/new-arrivals" className="hover:text-white">New Arrivals</Link></li>
            <li><Link to="/clearance" className="hover:text-white">Clearance</Link></li>
            <li><Link to="/categories" className="hover:text-white">Categories</Link></li>
          </ul>
        </div>

        <div>
          <div className="font-heading text-brand-yellow mb-3">Help</div>
          <ul className="space-y-2 text-sm text-slate-300">
            <li><Link to="/how-it-works" className="hover:text-white">How It Works</Link></li>
            <li><Link to="/pickup" className="hover:text-white">Drive-Through Pickup</Link></li>
            <li><Link to="/about" className="hover:text-white">About Us</Link></li>
            <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
            <li><Link to="/faq" className="hover:text-white">FAQ</Link></li>
            <li><Link to="/returns" className="hover:text-white">Returns</Link></li>
            <li><Link to="/store-policies" className="hover:text-white">Store Policies</Link></li>
            <li><Link to="/privacy" className="hover:text-white">Privacy</Link></li>
            <li><Link to="/terms" className="hover:text-white">Terms</Link></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-400">
          <div>© {new Date().getFullYear()} Barrhaven Dealswagon Outlet · dealswagon.ca</div>
          <div className="flex items-center gap-3">
            <SocialLinks settings={settings} iconClass="h-4 w-4" />
            <a href="mailto:hello@dealswagon.ca" data-testid="social-email-link" className="hover:text-white"><Mail className="h-4 w-4" /></a>
          </div>
        </div>
      </div>
    </footer>
  );
};
